import { useState, useMemo } from "react";
import { Calculator, X, TrendingUp, TrendingDown } from "lucide-react";

/**
 * Per-product margin calculator.
 * Lets user override selling price, ad cost per sale, shipping cost to customer,
 * and payment processing %, then shows net profit, margin %, and break-even ROAS.
 */
export default function MarginCalculator({ product, rank }) {
  const [open, setOpen] = useState(false);
  const [sellingPrice, setSellingPrice] = useState(product.selling_price_usd);
  const [cost, setCost] = useState(product.cost_price_usd);
  const [shippingCost, setShippingCost] = useState(4.99);
  const [adCostPerSale, setAdCostPerSale] = useState(8.0);
  const [processingPct, setProcessingPct] = useState(2.9);

  const calc = useMemo(() => {
    const sp = Number(sellingPrice) || 0;
    const c = Number(cost) || 0;
    const ship = Number(shippingCost) || 0;
    const ad = Number(adCostPerSale) || 0;
    const proc = sp * ((Number(processingPct) || 0) / 100);
    const netProfit = sp - c - ship - ad - proc;
    const netMarginPct = sp > 0 ? (netProfit / sp) * 100 : 0;
    const grossMarginPct = sp > 0 ? ((sp - c) / sp) * 100 : 0;
    const breakEvenRoas = ad > 0 ? sp / ad : 0;
    return {
      netProfit,
      netMarginPct,
      grossMarginPct,
      breakEvenRoas,
      processingFee: proc,
    };
  }, [sellingPrice, cost, shippingCost, adCostPerSale, processingPct]);

  const profitable = calc.netProfit > 0;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`open-calculator-${rank}`}
        className="w-full border border-zinc-900 bg-white text-zinc-900 text-xs font-bold uppercase tracking-widest py-3 px-4 flex items-center justify-center gap-2 hover:bg-zinc-900 hover:text-white transition mb-3"
      >
        <Calculator size={14} strokeWidth={2.5} />
        Open Margin Calculator
      </button>
    );
  }

  return (
    <div
      className="border border-zinc-900 bg-zinc-50 p-5 mb-3"
      data-testid={`calculator-panel-${rank}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calculator size={14} strokeWidth={2.5} />
          <span className="micro-label text-zinc-900">Margin Calculator</span>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid={`close-calculator-${rank}`}
          className="text-zinc-500 hover:text-zinc-900"
          aria-label="Close calculator"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <InputField
          label="Selling $"
          value={sellingPrice}
          onChange={setSellingPrice}
          testid={`calc-selling-${rank}`}
        />
        <InputField
          label="Cost $"
          value={cost}
          onChange={setCost}
          testid={`calc-cost-${rank}`}
        />
        <InputField
          label="Shipping $"
          value={shippingCost}
          onChange={setShippingCost}
          testid={`calc-shipping-${rank}`}
        />
        <InputField
          label="Ad / Sale $"
          value={adCostPerSale}
          onChange={setAdCostPerSale}
          testid={`calc-ad-${rank}`}
        />
        <InputField
          label="Processing %"
          value={processingPct}
          onChange={setProcessingPct}
          testid={`calc-processing-${rank}`}
          suffix="%"
        />
        <div className="flex flex-col justify-end">
          <span className="micro-label mb-1 text-[9px]">Proc fee</span>
          <span className="text-sm font-bold font-mono">${calc.processingFee.toFixed(2)}</span>
        </div>
      </div>

      <div
        className={`border-l-4 p-4 mb-3 ${
          profitable ? "klein-border bg-white" : "border-red-500 bg-red-50"
        }`}
        data-testid={`calc-result-${rank}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="micro-label mb-1">Net Profit / Unit</div>
            <div
              className={`text-3xl font-black tracking-tighter ${
                profitable ? "klein-text" : "text-red-600"
              }`}
              style={{ fontFamily: "Outfit" }}
            >
              {profitable ? "+" : ""}${calc.netProfit.toFixed(2)}
            </div>
          </div>
          <div className="text-right">
            <div className="micro-label mb-1">Net Margin</div>
            <div
              className={`text-3xl font-black tracking-tighter flex items-center gap-1 ${
                profitable ? "text-emerald-600" : "text-red-600"
              }`}
              style={{ fontFamily: "Outfit" }}
            >
              {profitable ? (
                <TrendingUp size={20} strokeWidth={3} />
              ) : (
                <TrendingDown size={20} strokeWidth={3} />
              )}
              {calc.netMarginPct.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-0 border border-zinc-200 bg-white">
        <div className="p-3 border-r border-zinc-200">
          <div className="micro-label text-[9px] mb-1">Gross Margin</div>
          <div className="text-lg font-bold font-mono">
            {calc.grossMarginPct.toFixed(1)}%
          </div>
        </div>
        <div className="p-3">
          <div className="micro-label text-[9px] mb-1">Break-even ROAS</div>
          <div className="text-lg font-bold font-mono">
            {calc.breakEvenRoas > 0 ? calc.breakEvenRoas.toFixed(2) : "—"}x
          </div>
        </div>
      </div>

      {!profitable && (
        <p className="text-[10px] text-red-700 mt-3 leading-relaxed">
          ⚠ Losing ${Math.abs(calc.netProfit).toFixed(2)} per unit. Reduce ad cost, raise selling price, or find a cheaper supplier.
        </p>
      )}
    </div>
  );
}

function InputField({ label, value, onChange, testid, suffix }) {
  return (
    <div className="flex flex-col">
      <label className="micro-label mb-1 text-[9px]">{label}</label>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-2 py-1.5 border border-zinc-300 bg-white text-sm font-mono font-bold focus:outline-none focus:border-[#002FA7]"
        />
        {suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 font-mono">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
